import { z } from 'zod';

import { serverGameRoundSchema, serverGameSolutionSchema } from './game-round.js';
import { richMessageSchema } from './rich-content.js';

/* -------------------------------------------------------------------------- */
/* Client → Server                                                            */
/* -------------------------------------------------------------------------- */

/** Granular per-session recording flags. Matches RecordingOptions in the backend. */
export const recordingOptionsSchema = z
  .object({
    audio: z.boolean().optional(),
    traces: z.boolean().optional(),
    logs: z.boolean().optional(),
    transcript: z.boolean().optional(),
  })
  .strict();

/** A boolean shorthand `false` disables every flag; `true` enables every flag. */
export const recordingOverrideSchema = z.union([z.boolean(), recordingOptionsSchema]);

/**
 * Optional bag of module-specific hints. Forwarded as-is to the module runtime
 * factory. Keys are module-defined.
 */
export const moduleHintsSchema = z.record(z.string(), z.unknown());
export type ModuleHints = z.infer<typeof moduleHintsSchema>;

export const clientStartSchema = z.object({
  type: z.literal('start'),
  moduleId: z.string().min(1).optional(),
  /** Slug of the module to run. Defaults server-side to general_assistant. */
  moduleSlug: z.string().min(1).optional(),
  /** Free-form hints for the module runtime (e.g. {mode,intensity}). */
  hints: moduleHintsSchema.optional(),
  recording: recordingOverrideSchema.optional(),
});

export type RecordingOverride = z.infer<typeof recordingOverrideSchema>;

/**
 * Cuánto sonó del turno del coach cuando se lo cortó (`ReplyPlayback`, ver
 * `heard-reply.ts`). Con esto el server deja en el historial sólo lo oído.
 */
export const replyPlaybackSchema = z.object({
  playedMs: z.number().nonnegative(),
  receivedMs: z.number().nonnegative(),
  complete: z.boolean(),
});

export const clientInterruptSchema = z.object({
  type: z.literal('interrupt'),
  interruptionId: z.string().min(1),
  /** Ausente si el coach no estaba sonando (nada que recortar). */
  playback: replyPlaybackSchema.optional(),
});

export const clientTextSchema = z.object({
  type: z.literal('text'),
  body: z.string().min(1),
});

/**
 * A partial transcript typed by a client that has no microphone — the session
 * simulator (`apps/backend/scripts/eval/memory-live-sim`) replaying the STT's
 * partials word by word, so what hangs off them (live recall's F3b prefetch)
 * runs as in a voice turn. The server pushes it as an `InterimTranscriptionFrame`
 * (the same frame the STT emits); the final words still arrive as `text`. No
 * more privilege than `text`: it never dispatches a reply.
 */
export const clientTextPartialSchema = z.object({
  type: z.literal('text.partial'),
  body: z.string(),
});

export const clientEndSchema = z.object({ type: z.literal('end') });

export const clientTurnStartSchema = z.object({ type: z.literal('turn-start') });
export const clientTurnStopSchema = z.object({ type: z.literal('turn-stop') });

/**
 * Cliente descarta el turno del user en curso (toggle/hold). El server cierra
 * la utterance en el STT sin commitear, no emite `TranscriptionFrame`, no
 * dispara el LLM, no cobra créditos. No deja huella en traces — semánticamente
 * "es como si el user nunca hubiera hablado en este turno". Sin ack.
 */
export const clientTurnDiscardSchema = z.object({ type: z.literal('turn-discard') });

/**
 * Cliente cancela el último turno YA DESPACHADO ("me arrepentí" / el STT lo
 * interpretó mal). El server antepone un `InterruptionFrame` (aborta reply
 * LLM + TTS) y poda el intercambio del historial del LLM
 * (`UserTurnCancelFrame` → `SharedContext.truncateCancelled`, idempotente);
 * el side-runner de correcciones aborta la extracción en vuelo. Sin ack —
 * el cliente ya cortó audio y burbujas localmente de forma optimista.
 */
export const clientTurnCancelSchema = z.object({ type: z.literal('turn-cancel') });

/**
 * El browser notifica al backend cuando termina de REPRODUCIR el audio del
 * bot (evento `'ended'` del `<audio>` / `MediaSource`). Permite al backend
 * cerrar el span `voice.bot_playback` con timestamp real (no estimado por
 * `audioSeconds`), separando visualmente "el bot está hablando" de "el
 * user está pensando antes de responder" en el waterfall del admin.
 */
export const clientPlaybackFinishedSchema = z.object({
  type: z.literal('playback-finished'),
  lessonReceiptId: z.string().min(1).optional(),
  lessonPlaybackFailed: z.boolean().optional(),
});

/**
 * Mid-session module state update (e.g. languages module/intensity flip).
 * The server applies it and the next turn re-bakes the system prompt.
 */
export const clientSessionUpdateSchema = z.object({
  type: z.literal('session.update'),
  patch: moduleHintsSchema,
});

/**
 * Cambio de voz on-the-fly. El cliente lo envía al elegir otra voz (y persiste
 * la elección via REST `PUT /api/personas/settings/module/:slug`). El server
 * recicla el TTS upstream (ElevenLabs o Gemini) con el nuevo voiceId; la
 * próxima respuesta del bot ya sale con la voz nueva sin reiniciar el WS.
 *
 * `min(1)`, NO `min(8)`: los ids de Gemini son nombres cortos ("Kore" = 4).
 * Con `min(8)` el transport rechazaba el mensaje y el swap nunca ocurría —
 * la charla seguía con la voz vieja hasta reabrirla. La validez de la voz
 * PARA EL PROVIDER la decide el server (`resolveVoiceForProvider`).
 */
export const clientVoiceChangeSchema = z.object({
  type: z.literal('voice.change'),
  voiceId: z.string().min(1).max(64),
  /** Nombre legible — opcional, solo para logs/telemetry. */
  personaName: z.string().min(1).max(60).optional(),
});

/**
 * El cliente pide pausar la sesión sin cerrarla: el server descarta audio
 * entrante y no abre nuevos turnos; el TTS en vuelo termina en server pero
 * el cliente lo congela localmente vía `AudioContext.suspend()` para
 * continuarlo sample-exact al reanudar. Los créditos quedan auto-parados
 * porque el meter se mueve por uso (LLM/TTS/STT), no por wall-clock.
 */
export const clientSessionPauseSchema = z.object({
  type: z.literal('session.pause'),
});

/** Reanuda una sesión previamente pausada. No-op si no estaba pausada. */
export const clientSessionResumeSchema = z.object({
  type: z.literal('session.resume'),
});

/**
 * Cambio de modo I/O (fase 28). Los 4 modos = matriz input×output con valores
 * `voice`/`text`. El server reconfigura STT/TTS bypass on-the-fly sin tirar la
 * sesión. La memoria/contexto/créditos siguen vivos. Default sesión = voice/voice.
 */
export const ioChannelSchema = z.enum(['voice', 'text']);
export const ioModeSchema = z.object({
  input: ioChannelSchema,
  output: ioChannelSchema,
});
export type IOChannel = z.infer<typeof ioChannelSchema>;
export type IOMode = z.infer<typeof ioModeSchema>;

export const clientModeSwitchSchema = z.object({
  type: z.literal('mode.switch'),
  mode: ioModeSchema,
});

/**
 * Texto tipeado por el user cuando `input='text'`. El SttProcessor en modo
 * bypass lo emite como TranscriptionFrame final.
 */
export const clientUserTextSchema = z.object({
  type: z.literal('user.text'),
  text: z.string().min(1).max(8000),
  /**
   * `true` si el texto NO lo produjo el user sino el botón "Inspirar" (el user
   * tocó "Usar esto"). El backend marca el turno para NO generar correcciones
   * sobre él: corregir una sugerencia de la propia app es incoherente (Fase 14C).
   */
  fromSuggestion: z.boolean().optional(),
});

/**
 * Toggle de traducción del texto del bot. `targetLang` opcional para cambiar
 * idioma destino; si se omite, se mantiene el guardado en `users.preferred_language`.
 */
export const clientTranslationToggleSchema = z.object({
  type: z.literal('translation.toggle'),
  enabled: z.boolean(),
  targetLang: z.string().length(2).optional(),
});

/**
 * Cambio de velocidad de habla del assistant. `rate` se pasa directo a
 * `voice_settings.speed` del provider TTS (ElevenLabs Flash v2.5 acepta
 * 0.7-1.2; preserva el pitch). Aplica al PRÓXIMO turno — el contexto del
 * turno en curso ya tiene su `voice_settings` enviado al init.
 *
 * El UI mapea labels "amigables" (0.5x/1x/1.5x) a estos valores reales del
 * provider. Persistido en localStorage por módulo en el cliente.
 */
export const clientSpeechRateChangeSchema = z.object({
  type: z.literal('speech-rate.change'),
  rate: z.number().min(0.7).max(1.2),
});

/**
 * Diagnóstico: cada cuánto la ventana de settle del mic (`MIC_SETTLE_MS`) le
 * come el arranque a quien contesta rápido. El cliente manda el acumulado de
 * la sesión en cada `turn-end`; el backend se queda con el último y lo escribe
 * como span `voice.mic_settle` al cerrar la sesión.
 *
 * Las dos cifras que importan van juntas a propósito: `bittenTurns` sin
 * `turns` no dice nada — la pregunta es "pasa en X de cada 100 turnos".
 *
 * Es cumulativo, no incremental: un mensaje perdido no descuadra la cuenta.
 * Ver `packages/voice-client/src/useVoiceSession/micSettleCounter.ts`.
 */
export const clientMicSettleReportSchema = z.object({
  type: z.literal('mic-settle-report'),
  /** Turnos del coach cerrados en la sesión (el denominador). */
  turns: z.number().int().min(0),
  /** De esos, en cuántos el settle descartó al menos un bloque CON audio. */
  bittenTurns: z.number().int().min(0),
  /** Bloques descartados con audio no-cero. */
  chunks: z.number().int().min(0),
  /** Milisegundos de audio no-cero que el settle descartó. */
  droppedMs: z.number().min(0),
});

/**
 * Reintentar el reply que falló (`{type:'error', code:'reply_failed'}`): el
 * server re-despacha EL MISMO contexto del turno fallido — el user no repite
 * nada y su mensaje no se duplica en el historial. Sin un fallo pendiente es
 * no-op (lo loguea el server).
 */
export const clientReplyRetrySchema = z.object({
  type: z.literal('reply.retry'),
});

export const clientWsMessageSchema = z.discriminatedUnion('type', [
  clientStartSchema,
  clientInterruptSchema,
  clientTextSchema,
  clientTextPartialSchema,
  clientEndSchema,
  clientTurnStartSchema,
  clientTurnStopSchema,
  clientTurnDiscardSchema,
  clientTurnCancelSchema,
  clientPlaybackFinishedSchema,
  clientSessionUpdateSchema,
  clientVoiceChangeSchema,
  clientSessionPauseSchema,
  clientSessionResumeSchema,
  clientModeSwitchSchema,
  clientUserTextSchema,
  clientTranslationToggleSchema,
  clientSpeechRateChangeSchema,
  clientMicSettleReportSchema,
  clientReplyRetrySchema,
]);

export type ClientStartMessage = z.infer<typeof clientStartSchema>;
export type ClientInterruptMessage = z.infer<typeof clientInterruptSchema>;
export type ClientTextMessage = z.infer<typeof clientTextSchema>;
export type ClientEndMessage = z.infer<typeof clientEndSchema>;
export type ClientTurnStartMessage = z.infer<typeof clientTurnStartSchema>;
export type ClientTurnStopMessage = z.infer<typeof clientTurnStopSchema>;
export type ClientTurnDiscardMessage = z.infer<typeof clientTurnDiscardSchema>;
export type ClientTurnCancelMessage = z.infer<typeof clientTurnCancelSchema>;
export type ClientPlaybackFinishedMessage = z.infer<typeof clientPlaybackFinishedSchema>;
export type ClientSessionUpdateMessage = z.infer<typeof clientSessionUpdateSchema>;
export type ClientVoiceChangeMessage = z.infer<typeof clientVoiceChangeSchema>;
export type ClientSessionPauseMessage = z.infer<typeof clientSessionPauseSchema>;
export type ClientSessionResumeMessage = z.infer<typeof clientSessionResumeSchema>;
export type ClientModeSwitchMessage = z.infer<typeof clientModeSwitchSchema>;
export type ClientUserTextMessage = z.infer<typeof clientUserTextSchema>;
export type ClientTranslationToggleMessage = z.infer<typeof clientTranslationToggleSchema>;
export type ClientSpeechRateChangeMessage = z.infer<typeof clientSpeechRateChangeSchema>;
export type ClientMicSettleReportMessage = z.infer<typeof clientMicSettleReportSchema>;
export type ClientReplyRetryMessage = z.infer<typeof clientReplyRetrySchema>;
export type ClientWsMessage = z.infer<typeof clientWsMessageSchema>;

/* -------------------------------------------------------------------------- */
/* Server → Client                                                            */
/* -------------------------------------------------------------------------- */

export const sessionStateSchema = z.enum(['listening', 'thinking', 'speaking', 'error', 'idle']);
export type SessionState = z.infer<typeof sessionStateSchema>;

export const serverStateSchema = z.object({
  type: z.literal('state'),
  value: sessionStateSchema,
});

export const serverTranscriptSchema = z
  .object({
    type: z.literal('transcript'),
    partial: z.string().optional(),
    final: z.string().optional(),
  })
  .refine((msg) => typeof msg.partial === 'string' || typeof msg.final === 'string', {
    message: 'transcript requires either partial or final',
  });

export const serverTokensSchema = z.object({
  type: z.literal('tokens'),
  delta: z.string(),
  /**
   * Modo realtime (S2S): id del ciclo de respuesta de OpenAI al que pertenece
   * el delta. El cliente lo usa para keyear el revelado de la burbuja del bot
   * (plan realtime §6, decisión #16): un delta con id distinto al de la
   * burbuja viva significa turno NUEVO → commit del anterior. Ausente en el
   * pipeline clásico (el karaoke/alignment maneja el revelado ahí).
   */
  responseId: z.string().min(1).optional(),
});

/**
 * Pipeline clásico: terminaron de llegar los `tokens` del turno del coach (el
 * LLM cerró su respuesta), aunque la voz siga sintetizándose y sonando — el
 * `turn-end` llega recién con el fin del audio, 1–2 s después. La charla
 * asistida arma ahí la frase modelo (Emmanuel, 16/10). En realtime no se manda.
 */
export const serverTokensDoneSchema = z.object({
  type: z.literal('tokens.done'),
});

export const serverTurnEndSchema = z.object({
  type: z.literal('turn-end'),
  /**
   * Modo realtime: id del ciclo de respuesta que cierra. Permite el commit
   * IDEMPOTENTE bajo solape (si un turno más nuevo ya abrió burbuja, este
   * turn-end viejo no la pisa). Ausente en el pipeline clásico.
   */
  responseId: z.string().min(1).optional(),
});

/**
 * Modo realtime: barge-in detectado por el server-VAD de OpenAI mientras el
 * bot hablaba — OpenAI ya canceló su respuesta; el cliente debe CORTAR el
 * playback local (`AudioPlayer.reset`), porque la generación va más rápida
 * que el playback y la cola del player tiene audio que ya no corresponde.
 * En el pipeline clásico no existe (half-duplex: el mic se gatea durante
 * `speaking`, nunca hay solape).
 */
export const serverInterruptedSchema = z.object({
  type: z.literal('interrupted'),
  /** Id del ciclo de respuesta cancelado (si se conocía al detectar el barge-in). */
  responseId: z.string().min(1).optional(),
});

/**
 * Fase 36 — sync alignment del TTS (karaoke). Cada chunk emitido por el
 * provider (ElevenLabs) trae el split caracter-por-caracter del segmento
 * con timestamps relativos al ARRANQUE DEL CONTEXTO TTS (turno del bot). El
 * cliente normaliza contra el `AudioContext.currentTime` capturado al primer
 * chunk de audio del turno para highlightear la palabra/letra activa.
 *
 * El arrays tienen el mismo `length`. `audioStartOffsetMs` es el offset
 * acumulado en ms del audio precedente del MISMO contexto al inicio del
 * chunk asociado a este alignment — el cliente lo suma a cada `charStartTimesMs[i]`
 * para anclar al timeline del context.
 */
export const serverTtsAlignmentSchema = z.object({
  type: z.literal('tts.alignment'),
  chars: z.array(z.string()).min(1),
  charStartTimesMs: z.array(z.number()).min(1),
  charDurationsMs: z.array(z.number()).min(1),
  audioStartOffsetMs: z.number().nonnegative(),
});

export const serverInterruptAckSchema = z.object({
  type: z.literal('interrupt-ack'),
  interruptionId: z.string().min(1),
});

export const serverErrorSchema = z.object({
  type: z.literal('error'),
  message: z.string(),
  code: z.string().optional(),
});

/**
 * `code` de `{type:'error'}` cuando falló el reply del coach (el provider del
 * LLM rechazó o se cortó). NO es un error de la sesión: el server cierra el
 * turno normal (`turn-end` + `state: listening`) y la sesión sigue viva — la
 * app muestra "El coach no pudo responder" con Reintentar (`reply.retry`).
 */
export const REPLY_FAILED_ERROR_CODE = 'reply_failed';

export const serverTransitionSchema = z.object({
  type: z.literal('transition'),
  target: z.string().min(1),
});

export const serverTaskUpdateSchema = z.object({
  type: z.literal('task-update'),
  task: z.record(z.string(), z.unknown()),
});

/**
 * Emitido por el server una sola vez al inicio del WS, justo después de que
 * el SessionStartFlow asigna el `sessionId`. El cliente lo usa para pedir
 * el resumen al endpoint correspondiente cuando la sesión termina.
 */
export const serverSessionStartedSchema = z.object({
  type: z.literal('session.started'),
  sessionId: z.string().min(1),
  /** Slug del módulo elegido (lo que el cliente mandó en `?moduleSlug=`). */
  moduleSlug: z.string().min(1),
  /**
   * Resumed-from session id (cuando el WS retomó una sesión previa). Best-effort.
   */
  resumedFrom: z.string().min(1).optional(),
  /**
   * Estado persistido de la traducción (de `conversation_prefs.translation`
   * + `users.preferred_language`). El cliente lo usa para precargar el toggle
   * de UI sin esperar a que el user lo active. Sin esto, F5 reseteaba el switch
   * al default mientras el backend ya tenía la pref correcta.
   */
  translation: z
    .object({
      enabled: z.boolean(),
      targetLang: z.string().length(2),
    })
    .optional(),
  /**
   * Texto inicial del asistente que el módulo precargó durante el handshake.
   * Usado por el módulo `videos`: cuando el user pega un link, el backend
   * llama a Gemini para extraer título + resumen del video, y lo devuelve
   * acá para que la primera bubble del bot ya tenga contenido sin esperar
   * un turno del user. Si está vacío / ausente, el cliente no agrega ningún
   * mensaje y se comporta como antes.
   */
  openerAssistantText: z.string().optional(),
  /**
   * Fase 31 — versión rich del opener. Cuando está presente, el cliente la
   * renderiza con `<RichMessageBody>`. `openerAssistantText` sigue
   * conviviendo como fallback string (derivado server-side via
   * `richToPlain(openerAssistantContent)`) — usado por screen readers,
   * persistencia legacy y clientes con bundle viejo.
   */
  openerAssistantContent: richMessageSchema.optional(),
  /**
   * `true` cuando la sesión graba el audio crudo por turno (RECORD_SESSION_AUDIO
   * + storage disponible). El cliente lo usa para gatear el botón de play del
   * audio REAL del usuario, que no tiene fallback de re-síntesis (a diferencia
   * del coach, que cae a `speakText`). Ausente/`false` → no se muestra ese botón.
   */
  audioRecording: z.boolean().optional(),
  /**
   * Modo de conversación resuelto SERVER-SIDE (plan realtime, decisión #20:
   * `module_config.coach.conversationMode`; el cliente NO lo elige — lo
   * aprende de acá). En `realtime` (S2S full-duplex) el cliente bypasea el
   * gate half-duplex del mic: el audio se manda SIEMPRE, el barge-in lo
   * maneja el server-VAD de OpenAI. Ausente = `classic` (compat con backend
   * viejo).
   */
  conversationMode: z.enum(['realtime', 'classic']).optional(),
  /**
   * TTS de la sesión, resuelto SERVER-SIDE por el routing del módulo
   * (`module_config.<slug>.modelOverrides.tts` → routing global). El cliente
   * lo usa para el selector de voz (voces del provider activo: ElevenLabs =
   * personas, Gemini = `GEMINI_TTS_VOICES`) y para saber si habrá karaoke:
   * `alignment:false` (Gemini) = no llegan `tts.alignment`, el revelado de la
   * burbuja va por tiempo. `voiceId` es la voz INICIAL (ya válida para el
   * provider). Ausente = backend viejo (ElevenLabs con alignment) o realtime.
   */
  tts: z
    .object({
      provider: z.enum(['elevenlabs', 'gemini']),
      model: z.string().min(1),
      voiceId: z.string().min(1),
      alignment: z.boolean(),
    })
    .optional(),
  /**
   * ¿Esta sesión PRODUCE correcciones? Lo decide el gate de montaje del
   * side-runner (`correctionsMountGate`): es `false` en el examen de nivel,
   * en A0 sin misión y en cualquier módulo sin `correctionsRunner`.
   *
   * El cliente lo necesita porque el skeleton "analizando…" de cada user
   * bubble se limpia con `corrections.done`, y ese mensaje sólo lo emite el
   * side-runner: sin montarlo, el cartel quedaba colgado bajo TODAS las
   * burbujas de la charla (card #318 — visto en el examen de nivel apenas el
   * gate de calibration lo apagó). Con `enabled:false` el cliente no marca
   * `correctionPending` y el cartel no aparece nunca.
   *
   * Ausente (backend viejo) = el cliente asume `true` — conducta previa.
   */
  corrections: z
    .object({
      enabled: z.boolean(),
    })
    .optional(),
});

/**
 * Fase 31 — tool result mid-sesión. Un tool produce **un bubble**, identificado
 * por `toolCallId`. Si llegan partials con el mismo `toolCallId`, el cliente
 * reemplaza el bubble in-place (no duplica). 1 invocación lógica = 1 bubble.
 *
 * Comparte el formato `RichMessage` con `openerAssistantContent`. Distinto canal
 * WS, mismo renderer en el cliente. El `name` legible va al header del bubble
 * `who:'tool'` (ej. "Web search", "Verb tense").
 *
 * `text` es la versión plain del `content`, derivada server-side via
 * `richToPlain()`. Sirve como fallback para accessibility y persistencia legacy.
 */
export const serverToolResultSchema = z.object({
  type: z.literal('tool.result'),
  toolCallId: z.string().min(1),
  name: z.string().min(1).max(80),
  content: richMessageSchema,
  text: z.string(),
  /** Si true, indica que es un partial — el server puede emitir más con el mismo toolCallId. */
  partial: z.boolean().optional(),
});

/**
 * Corrección puntual extraída del stream de LLM por el `CorrectionExtractor`.
 * Llega ANTES o EN PARALELO al `tokens` stream del turno y referencia el
 * último user utterance. El cliente la asocia al bubble del user
 * correspondiente y la renderiza con un bloque visual diferenciado del
 * texto conversacional del bot.
 */
export const serverCorrectionSchema = z.object({
  type: z.literal('correction'),
  /** Lo que dijo el user (citado por el coach). */
  said: z.string().min(1),
  /** La forma correcta. */
  fix: z.string().min(1),
  /**
   * Severidad de la corrección. Define el color en el cliente y el filtro por
   * sensibilidad. `suggestion` = NO es error (más natural / upgrade). Los 4
   * valores DEBEN coincidir con `CorrectionSeverity` de `@orborix/module-languages`
   * (store/types.ts). Opcional/ausente en mensajes WS legacy → el cliente
   * default a `structural` (fail-safe: mostrar como error antes que ocultar).
   */
  severity: z.enum(['communication-breaking', 'structural', 'minor', 'suggestion']).optional(),
  /** Explicación opcional en castellano. */
  explanation: z.string().nullable().optional(),
  /**
   * Texto del user message que disparó esta corrección. El cliente matchea
   * backward por este string para anclar el chip al bubble correcto (igual que
   * `pronunciation_correction`). Sin esto el cliente anclaba SIEMPRE al último
   * user bubble y la corrección del primer turno "se arrastraba" a todas las
   * burbujas. Opcional / ausente en mensajes WS legacy.
   */
  userText: z.string().optional(),
  /** Índice del turn (1-based) al que pertenece, si el server lo conoce. */
  turnIndex: z.number().int().optional(),
  /**
   * Catalog skills this correction may concern (W6, `praiseBlockingSkillIds`
   * in module-languages): the skill its said → fix pair names, else its
   * phenomenon's skill(s). Never shown — the client uses it only to withhold a
   * positive skill chip on the same turn (`skillPraiseBlocked`). Absent on
   * paths that do not classify (the in-reply correction tool) or when nothing
   * is known: the client then falls back to the evidence-overlap rule.
   */
  skillIds: z.array(z.string().min(1)).optional(),
});

/**
 * Why a turn's corrections analysis produced NO trustworthy verdict. The
 * runner kinds (`llm_error` … `incomplete_stream`) come from
 * `runExtractCorrections` (`CorrectionsRunnerFailure` in module-languages);
 * `internal_error` is the side-runner itself throwing after the call.
 */
export const correctionsFailureReasonSchema = z.enum([
  /** The provider call threw (network, 4xx/5xx, timeout). */
  'llm_error',
  /** The model answered without the forced `extract_corrections` tool call. */
  'no_tool_call',
  /** The tool call arrived but its arguments failed the schema. */
  'malformed_output',
  /** The stream ended without its final event. */
  'incomplete_stream',
  /** The side-runner threw while processing the verdict. */
  'internal_error',
]);
export type CorrectionsFailureReason = z.infer<typeof correctionsFailureReasonSchema>;

/**
 * Why a turn got no verdict BY DESIGN — none of these is a clean sentence.
 */
export const correctionsSkipReasonSchema = z.enum([
  /** The text came from the app ("Inspirar"): the app does not correct itself. */
  'suggestion_turn',
  /** A game is running: the analysis ran and was persisted, but chips are withheld. */
  'game_turn',
  /** The call does not correct (A0 objectives-only: corrections are off). */
  'corrections_off',
  /** A newer learner turn arrived first: this turn's corrections were cancelled. */
  'superseded',
  /**
   * Charla asistida: the learner SAID the model answer the app showed (or
   * tapped «Seguir»). The app does not correct its own sentence, and the turn
   * counts neither for the mission nor for the level (the distill skips it).
   */
  'assisted_turn',
]);
export type CorrectionsSkipReason = z.infer<typeof correctionsSkipReasonSchema>;

/**
 * The verdict of ONE turn's corrections analysis. Only `ok` means the chips
 * the client received for that turn are the complete answer — so `ok` with no
 * chips is the only "this sentence had no errors". `failed` and `skipped` mean
 * "we do not know": an empty chip list there must never read as clean.
 *
 * `verdictBy` says who judged an `ok` turn: the corrector LLM, or the Jev
 * pre-filter that skipped it on a clean read (`correctionPrefilterMode: live`).
 */
export const correctionsVerdictSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ok'), verdictBy: z.enum(['corrector', 'prefilter']) }),
  z.object({ status: z.literal('failed'), reason: correctionsFailureReasonSchema }),
  z.object({ status: z.literal('skipped'), reason: correctionsSkipReasonSchema }),
]);
export type CorrectionsVerdict = z.infer<typeof correctionsVerdictSchema>;

/**
 * "The corrections side-runner closed this turn" — exactly one per learner
 * turn the side-runner saw, except a turn the learner cancelled (its bubble is
 * already gone client-side). It always carries WHICH turn and the verdict:
 * before, it was a bare `{type}`, so "no errors" and "analysis failed" were
 * the same message and a failure read as a clean sentence.
 *
 * The client anchors it like a `correction` (by `userText`, the exact text the
 * analysis ran on), clears that bubble's "analizando…" skeleton and stores the
 * verdict on it. `turnIndex` is the same turn index the turn's `correction`
 * chips and DB rows carry; `null` only when the server had no turn to anchor
 * to (degenerate — logged server-side).
 */
export const serverCorrectionsDoneSchema = z.object({
  type: z.literal('corrections.done'),
  turnIndex: z.number().int().nonnegative().nullable(),
  userText: z.string(),
  verdict: correctionsVerdictSchema,
});

/**
 * Marker de "esta corrección se DIJO en voz alta" — la coda hablada del modo
 * manos libres (`conversationPrefs.voiceCorrections`). Se emite cuando el TTS
 * de la coda efectivamente arranca (no al encolarla: una coda cancelada por
 * barge-in o staleness no emite marker). El cliente lo usa para badgear el
 * chip correspondiente; `said`/`fix` identifican la corrección dentro del
 * bubble anclado por `turnIndex` (o backward-match si falta).
 */
export const serverCorrectionSpokenSchema = z.object({
  type: z.literal('correction.spoken'),
  said: z.string().min(1),
  fix: z.string().min(1),
  turnIndex: z.number().int().optional(),
});

/**
 * Progreso de un objetivo de misión (Práctica de Escenarios, B6). Lo emite el
 * side-runner de correcciones por turno (piggyback, sin LLM extra): el juez de
 * objetivos corre dentro de la misma llamada `extract_corrections`. El cliente
 * lo redispara como window event para el tracker EN VIVO, que es OPTIMISTA
 * (marca y no desmarca). El debrief al cierre es la fuente de verdad — esto es
 * sólo feedback inmediato. Sólo se emite con `met=true`.
 */
export const serverObjectiveProgressSchema = z.object({
  type: z.literal('objective.progress'),
  /** Id del objetivo (matchea el catálogo curado). */
  objectiveId: z.string().min(1),
  /** Cumplido este turno (siempre true por diseño — el tracker no desmarca). */
  met: z.boolean(),
  /** Frase literal del user que evidencia el objetivo, si la hay. */
  evidence: z.string().optional(),
  /** Índice del turn (1-based) en que se cumplió, si el server lo conoce. */
  turnIndex: z.number().int().optional(),
  /**
   * Demostraciones DISTINTAS acumuladas de este objetivo (fase 17.B, programa con
   * sub-unidades). Presente sólo cuando el objetivo pertenece a una sub-unidad con
   * anillo de reps; ausente para objetivos binarios (escenarios / units legacy).
   */
  count: z.number().int().nonnegative().optional(),
  /** Reps target del objetivo (anillo `count/reps`). Acompaña a `count`. */
  reps: z.number().int().positive().optional(),
  /**
   * Charla asistida: el objetivo se PRACTICÓ con la respuesta modelo. No cuenta
   * para la misión (el tracker no lo marca cumplido); sirve para ofrecer «Hacelo
   * solo» cuando ya se practicaron todos.
   */
  assisted: z.literal(true).optional(),
});

/**
 * Charla asistida (encargo 99): el veredicto de un turno de VOZ dicho contra la
 * respuesta modelo (`session.update { assistedExpect }`). Lo emite el
 * `AssistedTurnGate` del server con la vara tolerante (`compareAssisted`).
 *   - `accepted`: el turno siguió al coach (con `heard` como texto del alumno).
 *   - no `accepted`: el turno NO llegó al coach; el alumno repite o toca «Seguir».
 * `secondOpinion` = lo rescató (o lo confirmó) una segunda transcripción del
 * mismo audio con Scribe batch, sin contexto. `marks` son índices de palabras
 * VISIBLES de la frase modelo (las que faltaron o cambiaron).
 */
export const serverAssistedResultSchema = z.object({
  type: z.literal('assisted.result'),
  accepted: z.boolean(),
  state: z.enum(['ok', 'ok-warning', 'ok-restart', 'repeat', 'silence']),
  heard: z.string(),
  marks: z.array(z.number().int().nonnegative()),
  missing: z.array(z.string()),
  extra: z.array(z.string()),
  secondOpinion: z.boolean(),
});

/**
 * What a credit may show live (owner, 2026-10-07), decided server-side:
 *   - `chips`: the usual rules (a completion, "lo clavaste", a grammar rep);
 *   - `nail-only`: only "lo clavaste" — the own skill of the learner's radar
 *     focus judged although it is out of the free-talk chip scope;
 *   - `silent`: no live chip and no live celebration — vocabulary credits of
 *     the lexicon matcher (progress and the debrief still count them).
 */
export const SKILL_CREDIT_DISPLAYS = ['chips', 'nail-only', 'silent'] as const;
export type SkillCreditDisplay = (typeof SKILL_CREDIT_DISPLAYS)[number];

/**
 * One skill of the catalog the learner used in a turn (W5, decisions D6/D7/D9).
 * Product copy (`label`) is Spanish first. The client decides what to show
 * within `display`: progress is SILENT (D7), a `completedNow` with `celebrate`
 * is the moment to celebrate, a live chip is for `grammar` only and once per
 * skill per session (`skillPriorTurns`), and `nailedFocus` is the "you nailed
 * it" of a radar focus (D9).
 */
export const skillCreditSchema = z.object({
  skillId: z.string().min(1),
  label: z.object({ es: z.string().min(1), en: z.string().min(1) }),
  area: z.enum(['grammar', 'words', 'conversation']),
  level: z.enum(['A1', 'A2', 'B1', 'B2', 'C1', 'C2']),
  /**
   * Who credited it: the per-turn judge, the lexicon matcher, a lesson
   * objective, or a watched skill of a can-do capstone (plan 46).
   */
  source: z.enum(['judge', 'lexicon', 'lesson', 'capstone']),
  /** The learner's own words that showed it (a verbatim span of the turn). */
  evidence: z.string().min(1),
  /** The credit moved progress (only the first 2 of a skill per session do). */
  counted: z.boolean(),
  /** Counted reps so far, and the reps that complete the skill. */
  reps: z.number().int().nonnegative(),
  repsToComplete: z.number().int().positive(),
  completed: z.boolean(),
  /** THIS credit completed the skill. */
  completedNow: z.boolean(),
  /** The catalog says a completion of this skill is celebrated (D7). */
  celebrate: z.boolean(),
  /** What this credit may show live (see {@link SKILL_CREDIT_DISPLAYS}). */
  display: z.enum(SKILL_CREDIT_DISPLAYS),
  /**
   * The OTHER turns of this session (every connection, a reload included)
   * whose credits of the same skill were recorded before this one, ascending:
   * a skill's chip shows once per session, on the first of its credits whose
   * bubble showed a chip — later ones count silently (owner, 2026-10-07).
   * Same session scope as the reps (`voice_sessions.id`).
   */
  skillPriorTurns: z.array(z.number().int().nonnegative()),
  /**
   * The skill is linked to one of the learner's active radar foci (D9), or
   * `null`. `pattern` is the focus's canonical description; `label` its short
   * display label (`metadata.label`, ≤ 60 chars, es-AR), `null` on foci that
   * have none.
   *
   * `priorTurns`: the turns of THIS session whose credits nailed the same
   * focus before this one was recorded (every connection of the session, a
   * reload included), ascending. "Lo clavaste" is once per focus per session:
   * the client spends the focus on the earliest-recorded nail it actually
   * showed, so a reload never repeats it and a late judge of an earlier turn
   * never moves it.
   */
  nailedFocus: z
    .object({
      focusId: z.string().min(1),
      pattern: z.string(),
      label: z.string().min(1).nullable(),
      priorTurns: z.array(z.number().int().nonnegative()),
    })
    .nullable(),
});
export type SkillCredit = z.infer<typeof skillCreditSchema>;

/**
 * "The learner used these catalog skills in this turn" — at most one per
 * learner turn and source path, only when something was credited. Anchored to
 * the user bubble like `corrections.done` (by `userText`, the exact text the
 * judge read); `turnIndex` is the same turn index the turn's corrections carry.
 * Emitted only with `module_config.languages.skillCreditEnabled` on.
 */
export const serverSkillsCreditedSchema = z.object({
  type: z.literal('skills.credited'),
  turnIndex: z.number().int().nonnegative(),
  userText: z.string(),
  credits: z.array(skillCreditSchema).min(1),
});

/**
 * Sugerencias tap-or-speak de la red A0 (plan 33 §1.1). El server las emite
 * para el objetivo PENDIENTE de la misión: una vez al arrancar la charla
 * (objetivo 1) y de nuevo en cada AVANCE de objetivo (el side-runner detecta que
 * el puntero cambió). Deterministas y curadas (NO salen de un LLM); el `en` ya
 * trae los placeholders del perfil rellenados ("My name is Ana."). El cliente lo
 * redispara como window event para `useA0Suggestions`; al tocar una opción se
 * envía con `fromSuggestion` (no se corrige). `stage` = capa default de la red
 * (zero/full=Elegir · medium=Repetir · light=Decir-con-pista, §1.2). Sólo en `mode='a0'`.
 */
export const serverA0SuggestionsSchema = z.object({
  type: z.literal('a0.suggestions'),
  /** Objetivo pendiente al que apuntan estas sugerencias (matchea el catálogo). */
  objectiveId: z.string().min(1),
  /** Meta en español del paso ("Saludá al coach"). */
  goalEs: z.string().min(1),
  /** Andamiaje vigente (capa default de la red §1.1). */
  stage: z.enum(['zero', 'full', 'medium', 'light']),
  /** Opciones tap-or-speak (todas válidas y enviables). */
  suggestions: z.array(z.object({ en: z.string().min(1), es: z.string().min(1) })).min(1),
});

/**
 * Misión A0 COMPLETADA (plan 33 §1.3): el side-runner detecta que TODOS los
 * objetivos de la misión a0 se cumplieron. Para el boss-charla (examen-gate) el
 * front lo usa para gatear la graduación (POST /a0/boss-result → vuelve al motor
 * A0 que gradúa); para micro-charlas, para festejar. Se emite UNA vez por sesión.
 */
export const serverA0MissionCompleteSchema = z.object({
  type: z.literal('a0.mission.complete'),
});

/**
 * Avance de sub-unidad del Programa guiado (fase 17.B). El side-runner lo emite:
 *   - al ARRANCAR la sesión (`initial: true`) con la sub-unidad activa (resume
 *     point), para que el cliente arme el tracker de reps desde el turno 1;
 *   - en cada AVANCE (`initial: false`), cuando los objetivos de la activa se
 *     cumplieron y el puntero pasó a la siguiente → el cliente festeja y swapea.
 * `initial`/avance son optimistas; el juez de cierre persiste la verdad. Sólo se
 * emite en sesiones `mode='plan'` sobre units AUTORADAS (con sub-unidades).
 */
/** Confirmación persistida de UNA parte autorada; nunca se deriva del anillo cliente. */
export const lessonCompletionSchema = z.object({
  receiptId: z.string().min(1),
  unitId: z.string().min(1),
  subUnitId: z.string().min(1),
  titleEs: z.string().min(1),
  review: z.boolean(),
  unitComplete: z.boolean(),
  nextSubUnitId: z.string().nullable(),
  nextTitleEs: z.string().nullable(),
  phase: z.enum(['choice', 'chat', 'closing', 'closed']),
  closingText: z.string().optional(),
  closingOutcome: z.enum(['delivered', 'failed', 'disconnected', 'timeout', 'recovered']).optional(),
  /**
   * Present when the session that ends is a curated role-play, not a lesson
   * (encargo 104: one ending for both). `unitId`/`subUnitId` then carry the
   * scenario id; `bonusDone` = labels of the bonus objectives the learner met.
   */
  scenario: z
    .object({ scenarioId: z.string().min(1), bonusDone: z.array(z.string().min(1)) })
    .optional(),
});
export type LessonCompletion = z.infer<typeof lessonCompletionSchema>;
export const serverLessonPlaybackAwaitSchema = z.object({ type: z.literal('lesson.playback.await'), receiptId: z.string().min(1) });

export const serverLessonCompletedSchema = lessonCompletionSchema.extend({
  type: z.literal('lesson.completed'),
});

export const serverSubunitAdvancedSchema = z.object({
  type: z.literal('subunit.advanced'),
  unitId: z.string().min(1),
  subUnitId: z.string().min(1),
  /** Posición 1..N de la sub-unidad activa. */
  order: z.number().int().positive(),
  /** Total de sub-unidades de la unit. */
  total: z.number().int().positive(),
  titleEs: z.string().min(1),
  titleEn: z.string().min(1),
  /**
   * De qué trata la sub-unidad, en español (1-2 frases del currículum). speak lo
   * muestra como línea de intro cuando se entra al Programa sin pasar por su
   * pantalla. Opcional por la misma razón que `review`; el backend lo manda
   * siempre.
   */
  blurbEs: z.string().min(1).optional(),
  /**
   * `true` = REPASO de una sub-unidad ya aprobada: los `count` arrancan en 0 (se
   * practica de nuevo, con los `reps` de hoy) y nada baja el sello. Opcional en
   * el schema para no romper clientes viejos (una app publicada puede traer un
   * bundle previo); el backend lo manda siempre.
   */
  review: z.boolean().optional(),
  /** Objetivos de la sub-unidad: id `u-<idx>`, criterio (inglés), reps target y
   *  `count` = demos ya acumuladas (resume point — distinto de 0 al reanudar).
   *  `label` = what the tracker shows instead of the criterion: the catalog
   *  skill the objective credits. Only a can-do lesson sends it (plan 46). */
  objectives: z
    .array(
      z.object({
        id: z.string().min(1),
        criterion: z.string().min(1),
        label: z.object({ es: z.string().min(1), en: z.string().min(1) }).optional(),
        reps: z.number().int().positive(),
        count: z.number().int().nonnegative().optional(),
      }),
    )
    .min(1),
  /** `true` si es la última sub-unidad (al completarla, ilumina el FAB de cierre). */
  isFinal: z.boolean(),
  /**
   * `true` si completar ESTA sub-unidad domina la unit (no es repaso y todas las
   * demás ya están completas). Es lo que habilita el festejo de unidad en vivo —
   * `isFinal` no alcanza. Opcional por la misma razón que `review` (bundles
   * viejos); el backend lo manda siempre.
   */
  completesUnit: z.boolean().optional(),
  /** `true` = emisión inicial (sin festejo); `false` = avance real (festejo). */
  initial: z.boolean(),
});

/**
 * Reporte de Pronunciation Assessment (Azure) para un turno del user. Llega
 * después del `transcript.final` y referencia esa misma utterance. El cliente
 * lo guarda en el bubble del user para mostrar scores agregados; un mensaje
 * separado `pronunciation_correction` (emitido por el coach) puede acompañar
 * con correcciones puntuales por palabra.
 *
 * Cuando el feature está OFF este mensaje no se emite.
 */
const pronunciationQualificationsSchema = z.object({
  cefr: z.string().optional(),
  ielts: z.number().optional(),
  pte: z.number().optional(),
  toeic: z.number().optional(),
});

export const serverPronunciationReportSchema = z.object({
  type: z.literal('pronunciation_report'),
  /** Texto del user al que pertenece este reporte. */
  userText: z.string().min(1),
  /**
   * Provider que produjo el reporte. Opcional para backwards-compat con
   * clients y eventos históricos (azure pre-migración 24). Valores actuales:
   * 'speechace'. Futuro: 'speechsuper' | 'languageconfidence'.
   */
  provider: z.string().optional(),
  overall: z.object({
    accuracy: z.number(),
    fluency: z.number(),
    completeness: z.number(),
    prosody: z.number(),
    pronScore: z.number(),
  }),
  words: z.array(
    z.object({
      word: z.string(),
      accuracy: z.number(),
      errorType: z.string(),
      phonemes: z.array(
        z.object({
          ipa: z.string(),
          accuracy: z.number(),
          soundMostLike: z.string().optional(),
          stressLevel: z.number().optional(),
          stressScore: z.number().optional(),
        }),
      ),
      qualifications: pronunciationQualificationsSchema.optional(),
    }),
  ),
  qualifications: pronunciationQualificationsSchema.optional(),
});

/**
 * Corrección puntual de pronunciación extraída del stream del LLM por el
 * `PronunciationCorrectionExtractor`. Espejo semántico de
 * `serverCorrectionSchema` pero para fonética: `said_ipa` vs `target_ipa`,
 * `tip` corto en castellano, y un `detail` opcional con explicación más
 * extensa para el modo "Profundizar".
 */
export const serverPronunciationCorrectionSchema = z.object({
  type: z.literal('pronunciation_correction'),
  /** La palabra evaluada. */
  word: z.string().min(1),
  /** IPA de lo que el user efectivamente dijo. */
  saidIpa: z.string().min(1),
  /** IPA target (forma estándar). */
  targetIpa: z.string().min(1),
  /** Tip corto (≤120 chars) en castellano rioplatense. */
  tip: z.string().min(1),
  /** Explicación extendida para vista "Profundizar". Opcional. */
  detail: z.string().nullable().optional(),
  /**
   * Texto del user message que disparó esta corrección. El cliente matchea
   * backward por este string para anclar el bloque PA al bubble correcto
   * (en lugar de "el último user bubble en estado"). Opcional / ausente en
   * mensajes WS legacy o cuando el extractor no pudo captar contexto.
   */
  userText: z.string().optional(),
  /**
   * WordReport del provider PA (SpeechAce) para esta word específica.
   * Embebido para eliminar el matching cliente. Cuando el LLM corrige un
   * word que no aparece en el report (caso raro), queda undefined.
   */
  wordReport: z
    .object({
      accuracy: z.number(),
      errorType: z.string(),
      phonemes: z.array(
        z.object({
          ipa: z.string(),
          accuracy: z.number(),
          soundMostLike: z.string().optional(),
          stressLevel: z.number().optional(),
          stressScore: z.number().optional(),
        }),
      ),
      qualifications: pronunciationQualificationsSchema.optional(),
    })
    .optional(),
});

/**
 * Emitido tras cerrar cada turno cuando el costing está wired. Permite a
 * la UI actualizar el saldo / HUD sin pegarle al backend.
 */
export const serverSessionCostUpdatedSchema = z.object({
  type: z.literal('session.cost.updated'),
  sessionId: z.string().min(1),
  turnId: z.string().min(1),
  /** Costo del turno (nano-USD positivo, como string para preservar bigint). */
  deltaNano: z.string().min(1),
  /** Costo acumulado de la sesión hasta este turno. */
  totalNano: z.string().min(1),
  breakdown: z
    .object({
      llmNano: z.string(),
      ttsNano: z.string(),
      sttNano: z.string(),
      /** Add-on de Pronunciation Assessment. Ausente o "0" cuando PA está OFF. */
      pronunciationNano: z.string().optional(),
    })
    .optional(),
});

/**
 * Emitido cuando el server bloquea la sesión: por saldo insuficiente (WS se
 * cierra con 4402) o por el safety router de coach en nivel 4 (WS se cierra
 * con 4403; el cliente muestra el overlay de crisis).
 */
export const serverSessionBlockedSchema = z.object({
  type: z.literal('session.blocked'),
  reason: z.enum(['insufficient_credits', 'coach_safety_l4']),
  sessionId: z.string().optional(),
  level: z.number().int().optional(),
  balanceNano: z.string().optional(),
  minRequiredNano: z.string().optional(),
});

/**
 * Evento del safety router de coach (niveles 1-3; el 4 llega además como
 * `session.blocked`). El cliente lo usa para la card/banner de ayuda humana —
 * la intervención hablada ya viaja por el path normal del LLM (el interceptor
 * sintetiza la respuesta en niveles 2-4).
 */
export const serverSessionSafetySchema = z.object({
  type: z.literal('session.safety'),
  sessionId: z.string(),
  level: z.number().int().min(1).max(4),
  intervention: z.object({ kind: z.string(), body: z.string() }).nullable(),
  resources: z.array(
    z.object({
      countryCode: z.string(),
      name: z.string(),
      phone: z.string(),
      url: z.string().nullable(),
      language: z.string(),
    }),
  ),
  matchedKeywords: z.array(z.string()),
});

/**
 * El server señala que la sesión terminó por iniciativa propia (no por un
 * `end`/cierre del cliente). Hoy lo usa Scout: cuando la entrevistadora llama
 * la tool `end_interview`, el backend emite esto para que el cliente
 * transicione a la pantalla de cierre una vez que el audio de la despedida
 * termina de reproducirse. El server NO cierra el WS al emitirlo — deja que
 * los chunks de TTS de la despedida terminen de streamear; el cliente cierra
 * la sesión cuando corresponde. `reason` es libre para futuros motivos
 * (`completed`, `max_duration`, …).
 */
export const serverSessionEndedSchema = z.object({
  type: z.literal('session.ended'),
  reason: z.string().min(1),
});

/**
 * Preaviso del cierre por inactividad (watch idle-no-speech): el server lo
 * emite un margen fijo ANTES del `session.ended` para que la UI muestre un
 * countdown accionable ("decí algo para seguir"). Si el user habla después
 * del preaviso, el server emite `session.closing.cancelled` y el cierre no
 * ocurre. `inSeconds` = segundos restantes al momento de emisión.
 */
export const serverSessionClosingSchema = z.object({
  type: z.literal('session.closing'),
  inSeconds: z.number().int().positive(),
  reason: z.string().min(1),
});

/** El preaviso de cierre quedó sin efecto (el user dio señal de vida). */
export const serverSessionClosingCancelledSchema = z.object({
  type: z.literal('session.closing.cancelled'),
});

/**
 * Ack del server al `session.pause` del cliente. La UI espera este mensaje
 * antes de marcar el estado visual `paused` para no mostrar feedback en
 * vacío si el server estaba caído.
 */
export const serverSessionPausedSchema = z.object({
  type: z.literal('session.paused'),
});

/** Ack del server al `session.resume`. */
export const serverSessionResumedSchema = z.object({
  type: z.literal('session.resumed'),
});

/**
 * Ack al `mode.switch` del cliente. Permite a la UI confirmar el modo aplicado
 * y revertir el optimistic update si el server lo rechaza por alguna razón.
 */
export const serverModeChangedSchema = z.object({
  type: z.literal('mode.changed'),
  mode: ioModeSchema,
});

/**
 * Caption del bot. Siempre se emite (vital para chat stream + traducción)
 * independientemente de si el output es voz o texto. La UI ya consume `tokens`
 * para stream de texto del bot; este mensaje sirve como evento de "turno
 * cerrado" con el texto completo y un `turnId` estable.
 */
export const serverLlmTextSchema = z.object({
  type: z.literal('llm.text'),
  turnId: z.string().min(1),
  text: z.string(),
  partial: z.boolean().optional(),
});

/**
 * Traducción del último turno del bot. Llega después del texto completo
 * (post-`turn-end`/`llm.text`).
 */
export const serverLlmTranslationSchema = z.object({
  type: z.literal('llm.translation'),
  turnId: z.string().min(1),
  translatedText: z.string(),
  targetLang: z.string().length(2),
});

/** Ack al `translation.toggle` con el estado efectivo. */
export const serverTranslationStateSchema = z.object({
  type: z.literal('translation.state'),
  enabled: z.boolean(),
  targetLang: z.string().length(2),
});

/**
 * Ack al `speech-rate.change` con el rate efectivo aplicado (puede haber
 * sido clampeado al rango del provider 0.7-1.2). El cliente lo usa para
 * reconciliar el state optimistic si difiere.
 */
export const serverSpeechRateChangedSchema = z.object({
  type: z.literal('speech-rate.changed'),
  rate: z.number().min(0.7).max(1.2),
});

/**
 * Error del módulo `videos` en la fase de arranque (eager-load contra Gemini
 * fallido, video que excede el cap, etc.). Se emite ANTES de cerrar el WS con
 * código 4422 para que el cliente pueda surfacear `message` en el welcome.
 */
export const serverVideoErrorSchema = z.object({
  type: z.literal('video.error'),
  reason: z.enum(['load_failed']),
  message: z.string().min(1),
});

/**
 * El coach arrancó una búsqueda web nativa server-side en este turno. La UI lo
 * usa para mostrar "Navegando la web…" mientras tarda. `query` es best-effort.
 */
export const serverWebSearchStartedSchema = z.object({
  type: z.literal('web_search.started'),
  query: z.string().optional(),
});

/** Fuente citada por la búsqueda web (para renderizar como cita). */
export const webSearchSourceSchema = z.object({
  title: z.string(),
  url: z.string(),
});

/** Fuentes que el coach usó en la búsqueda web de este turno. */
export const serverWebSearchSourcesSchema = z.object({
  type: z.literal('web_search.sources'),
  sources: z.array(webSearchSourceSchema),
});

/**
 * Orborix Fit (F2) — una serie del workout quedó registrada (extractor por voz o
 * tap). El cliente la tilda en vivo en la card del ejercicio en curso.
 * `exerciseCompleted`/`workoutCompleted` evitan eventos extra: el front avanza al
 * próximo ejercicio / cierra el día con estos flags.
 */
export const serverFitSetLoggedSchema = z.object({
  type: z.literal('fit.set.logged'),
  workoutId: z.string(),
  exerciseId: z.string(),
  setNumber: z.number().int().min(1),
  reps: z.number().int().min(1),
  kg: z.number().nullable(),
  rir: z.number().int().nullable().optional(),
  source: z.enum(['voice', 'tap']),
  exerciseCompleted: z.boolean(),
  workoutCompleted: z.boolean(),
});

/**
 * Fit (F2) — se aplicó una sustitución al workout del día (por voz: "la barra
 * está ocupada"). El cliente refresca la card del ejercicio en curso.
 */
export const serverFitSubstitutionAppliedSchema = z.object({
  type: z.literal('fit.substitution.applied'),
  workoutId: z.string(),
  fromExerciseId: z.string(),
  toExerciseId: z.string(),
  toName: z.string(),
  reason: z.string().optional(),
});

/**
 * Fit (F2) — la serie recién registrada es un récord personal del ejercicio. El
 * cliente dispara el overlay ¡PR! (animación prPop del prototipo).
 */
export const serverFitPrSchema = z.object({
  type: z.literal('fit.pr'),
  workoutId: z.string(),
  exerciseId: z.string(),
  exerciseName: z.string(),
  kg: z.number().nullable(),
  reps: z.number().int().min(1),
  metric: z.enum(['e1rm', 'reps']),
});

/**
 * Fit (F2d) — el user le pidió al PT rearmar el plan de fondo y el server
 * calculó si hay rework posible. El cliente muestra la card de oferta
 * (proponer→confirmar; jamás se auto-aplica). `available: false` = card
 * informativa sin CTA (`reason`: sin semanas completamente pendientes / sin
 * programa activo).
 */
export const serverFitPlanReworkOfferedSchema = z.union([
  z.object({
    type: z.literal('fit.plan.rework.offered'),
    available: z.literal(true),
    /** Primera semana completamente pendiente (donde arrancaría el rework). */
    fromWeek: z.number().int().min(2),
    /** Semanas del programa desde fromWeek hasta el final (lo reemplazable). */
    remainingWeeks: z.number().int().min(1),
  }),
  z.object({
    type: z.literal('fit.plan.rework.offered'),
    available: z.literal(false),
    reason: z.enum(['no_pending_week', 'no_active_program']),
  }),
]);

export const serverWsMessageSchema = z.union([
  serverStateSchema,
  serverTranscriptSchema,
  serverTokensSchema,
  serverTokensDoneSchema,
  serverTurnEndSchema,
  serverInterruptedSchema,
  serverInterruptAckSchema,
  serverErrorSchema,
  serverTransitionSchema,
  serverTaskUpdateSchema,
  serverSessionStartedSchema,
  serverCorrectionSchema,
  serverCorrectionsDoneSchema,
  serverCorrectionSpokenSchema,
  serverObjectiveProgressSchema,
  serverAssistedResultSchema,
  serverSkillsCreditedSchema,
  serverA0SuggestionsSchema,
  serverA0MissionCompleteSchema,
  serverSubunitAdvancedSchema,
  serverLessonCompletedSchema,
  serverLessonPlaybackAwaitSchema,
  serverPronunciationReportSchema,
  serverPronunciationCorrectionSchema,
  serverSessionCostUpdatedSchema,
  serverSessionBlockedSchema,
  serverSessionSafetySchema,
  serverSessionEndedSchema,
  serverSessionClosingSchema,
  serverSessionClosingCancelledSchema,
  serverSessionPausedSchema,
  serverSessionResumedSchema,
  serverModeChangedSchema,
  serverLlmTextSchema,
  serverLlmTranslationSchema,
  serverTranslationStateSchema,
  serverSpeechRateChangedSchema,
  serverToolResultSchema,
  serverVideoErrorSchema,
  serverTtsAlignmentSchema,
  serverWebSearchStartedSchema,
  serverWebSearchSourcesSchema,
  serverFitSetLoggedSchema,
  serverFitSubstitutionAppliedSchema,
  serverFitPrSchema,
  serverFitPlanReworkOfferedSchema,
  serverGameRoundSchema,
  serverGameSolutionSchema,
]);

export type ServerStateMessage = z.infer<typeof serverStateSchema>;
export type ServerTranscriptMessage = z.infer<typeof serverTranscriptSchema>;
export type ServerTokensMessage = z.infer<typeof serverTokensSchema>;
export type ServerTokensDoneMessage = z.infer<typeof serverTokensDoneSchema>;
export type ServerTurnEndMessage = z.infer<typeof serverTurnEndSchema>;
export type ServerInterruptedMessage = z.infer<typeof serverInterruptedSchema>;
export type ServerInterruptAckMessage = z.infer<typeof serverInterruptAckSchema>;
export type ServerErrorMessage = z.infer<typeof serverErrorSchema>;
export type ServerTransitionMessage = z.infer<typeof serverTransitionSchema>;
export type ServerTaskUpdateMessage = z.infer<typeof serverTaskUpdateSchema>;
export type ServerSessionStartedMessage = z.infer<typeof serverSessionStartedSchema>;
export type ServerCorrectionMessage = z.infer<typeof serverCorrectionSchema>;
export type ServerCorrectionsDoneMessage = z.infer<typeof serverCorrectionsDoneSchema>;
export type ServerCorrectionSpokenMessage = z.infer<typeof serverCorrectionSpokenSchema>;
export type ServerObjectiveProgressMessage = z.infer<typeof serverObjectiveProgressSchema>;
export type ServerAssistedResultMessage = z.infer<typeof serverAssistedResultSchema>;
export type ServerSkillsCreditedMessage = z.infer<typeof serverSkillsCreditedSchema>;
export type ServerA0SuggestionsMessage = z.infer<typeof serverA0SuggestionsSchema>;
export type ServerA0MissionCompleteMessage = z.infer<typeof serverA0MissionCompleteSchema>;
export type ServerSubunitAdvancedMessage = z.infer<typeof serverSubunitAdvancedSchema>;
export type ServerPronunciationReportMessage = z.infer<typeof serverPronunciationReportSchema>;
export type ServerPronunciationCorrectionMessage = z.infer<
  typeof serverPronunciationCorrectionSchema
>;
export type ServerSessionCostUpdatedMessage = z.infer<typeof serverSessionCostUpdatedSchema>;
export type ServerSessionBlockedMessage = z.infer<typeof serverSessionBlockedSchema>;
export type ServerSessionSafetyMessage = z.infer<typeof serverSessionSafetySchema>;
export type ServerSessionEndedMessage = z.infer<typeof serverSessionEndedSchema>;
export type ServerSessionClosingMessage = z.infer<typeof serverSessionClosingSchema>;
export type ServerSessionClosingCancelledMessage = z.infer<
  typeof serverSessionClosingCancelledSchema
>;
export type ServerSessionPausedMessage = z.infer<typeof serverSessionPausedSchema>;
export type ServerSessionResumedMessage = z.infer<typeof serverSessionResumedSchema>;
export type ServerModeChangedMessage = z.infer<typeof serverModeChangedSchema>;
export type ServerLlmTextMessage = z.infer<typeof serverLlmTextSchema>;
export type ServerLlmTranslationMessage = z.infer<typeof serverLlmTranslationSchema>;
export type ServerTranslationStateMessage = z.infer<typeof serverTranslationStateSchema>;
export type ServerSpeechRateChangedMessage = z.infer<typeof serverSpeechRateChangedSchema>;
export type ServerToolResultMessage = z.infer<typeof serverToolResultSchema>;
export type ServerVideoErrorMessage = z.infer<typeof serverVideoErrorSchema>;
export type ServerTtsAlignmentMessage = z.infer<typeof serverTtsAlignmentSchema>;
export type WebSearchSource = z.infer<typeof webSearchSourceSchema>;
export type ServerWebSearchStartedMessage = z.infer<typeof serverWebSearchStartedSchema>;
export type ServerWebSearchSourcesMessage = z.infer<typeof serverWebSearchSourcesSchema>;
export type ServerFitSetLoggedMessage = z.infer<typeof serverFitSetLoggedSchema>;
export type ServerFitSubstitutionAppliedMessage = z.infer<
  typeof serverFitSubstitutionAppliedSchema
>;
export type ServerFitPrMessage = z.infer<typeof serverFitPrSchema>;
export type ServerFitPlanReworkOfferedMessage = z.infer<typeof serverFitPlanReworkOfferedSchema>;
export type ServerWsMessage = z.infer<typeof serverWsMessageSchema>;

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

export function parseClientWsMessage(raw: string): ClientWsMessage {
  const parsed: unknown = JSON.parse(raw);
  return clientWsMessageSchema.parse(parsed);
}

export function parseServerWsMessage(raw: string): ServerWsMessage {
  const parsed: unknown = JSON.parse(raw);
  return serverWsMessageSchema.parse(parsed);
}

export function encodeServerWsMessage(message: ServerWsMessage): string {
  return JSON.stringify(message);
}

export function encodeClientWsMessage(message: ClientWsMessage): string {
  return JSON.stringify(message);
}
